import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { SpinnerService } from '@osc-utils/services/spinner.service';
import { Subscription } from 'rxjs';
import { ElementType, PropOrElemType } from 'src/app/_objects/element-type';
import { PropertyPanelDef } from 'src/app/_objects/interfaces/property-panel';
import { ValidationErrorDetails } from 'src/app/_objects/report-element';
import { ReportProperties } from 'src/app/_objects/report-properties';
import {
  ReportCustomElements,
  ReportTemplate,
  ResponseIdList,
  TemplateDefinition,
  TemplateImage,
  TemplatePageSection,
  UpdateIdList,
} from 'src/app/_objects/report-template';
import { ReportUnits } from 'src/app/_objects/report-units';
import { SubjectModelListItem } from 'src/app/_objects/subject-model';
import { UtilsService } from 'src/app/_services/_utils.service';
import { CommonSnackBarService } from 'src/app/_services/common-snack-bar.service';
import {
  MousePoint,
  Notifier,
  PropertyPreviewMetadata,
  ReportPreviewMetadata,
  ReportTemplateDesignService,
} from 'src/app/_services/report-template-design.service';
import { SubjectModelService } from 'src/app/_services/subject-model.service';
import { ERROR_MESSAGE, REGULAR_EXPRESSION, REPORT_TYPES } from 'src/app/app.constants';
import {
  EditableTextsValidationDialogComponent
} from 'src/app/common-dialog/inspection-report-edit-text-validation-dialog/inspection-report-edit-text-validation-dialog.component';
import {
  ReportTemplateEditPropertyDialogComponent
} from 'src/app/common-dialog/report-template-edit-property-dialog/report-template-edit-property-dialog.component';
import {
  ReportTemplateImageUploadDialogComponent
} from 'src/app/common-dialog/report-template-image-upload-dialog/report-template-image-upload-dialog.component';
import { SubjectModelWrapper } from 'src/app/helpers/subject-model-wrapper';
import {
  REPORT_TEMPLATE___APPLICATION_TYPE,
  StorageService,
} from '../../_services/storage.service';
import { CustomValidators } from '../../helpers/custom-validator';
import { JSONSyntaxValidator } from '../../helpers/json-syntax-validator';
import { ReportComponentService } from 'src/app/_services/report-component.service';
import { CommonDeleteDialogComponent } from 'src/app/common-dialog/common-delete-dialog/common-delete-dialog.component';
import { LangUpdateService } from 'src/app/_services/lang-update.service';
import { TranslateService } from '@ngx-translate/core';

namespace ButtonColors {
  export const Selected = 'accent';
  export const None = 'primary';
}

@Component({
  selector: 'app-report-preview',
  templateUrl: './report-preview.component.html',
  styleUrls: ['./report-preview.component.scss'],
  standalone: false
})
export class ReportPreviewComponent implements OnInit, OnDestroy {
  @ViewChild('navigation') public navigation: MatSidenav;
  constructor(
    public designService: ReportTemplateDesignService,
    private dialog: MatDialog,
    private router: Router,
    private subjectModelService: SubjectModelService,
    private spinnerService: SpinnerService,
    private utilsService: UtilsService,
    private storageService: StorageService,
    private snackBar: CommonSnackBarService,
    private eleref: ElementRef,
    private reportComponentService: ReportComponentService,
    private translate: TranslateService,
    private langService: LangUpdateService

  ) {
    this.langService.prepare(
      this,
      this.translate,
      (res) => {
        this.applications[0] = this.translate.instant('reportPreview.ViSOL');
        this.applications[1] = this.translate.instant('reportPreview.IPM');
        this.applications[2] = this.translate.instant('reportPreview.Generic');

        this.reportType = this.translate.instant('objects.' + REPORT_TYPES.find(x => x.value == this.designService.getTemplateReportType()).label);

        this.translatedEquipmentType =
          Array.isArray(this.equipmentType)
            ?
            this.equipmentType.map((r) => { return this.translate.instant('objects.' + r); }).join('\n') :
            (this.equipmentType ?
              (this.equipmentType as string).split(',').map((r) => { return this.translate.instant('objects.' + r); }).join(',') :
              '');
      }
    );
    this.notifier = {
      componentName: 'report-preview.component',
      ids: { pageId: '', elementId: '' },
    };

    this.permissionForActions = {
      page: {
        add: true,
        selectAll: false,
      },
      section: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      text: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      line: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      image: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      rectangle: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      table: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      interactiveIso: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      summary: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      subtotal: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      picture: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      dataPlate: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      measurementPoint: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      visualEvents: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      signature: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      selection: {
        delete: true,
      },
      dropDownList: {
        draw: true,
        selectAll: false,
        selectByPage: false,
      },
      overall: {
        undo: false,
        redo: false,
      },
    };

    this.buttonColors = {
      text: ButtonColors.None,
      line: ButtonColors.None,
      image: ButtonColors.None,
      rectangle: ButtonColors.None,
      summary: ButtonColors.None,
      subtotal: ButtonColors.None,
      picture: ButtonColors.None,
      dataPlate: ButtonColors.None,
      measurementPoint: ButtonColors.None,
      visualEvents: ButtonColors.None,
      signature: ButtonColors.None,
      section: ButtonColors.None,
      table: ButtonColors.None,
      interactiveIso: ButtonColors.None,
      dropDownList: ButtonColors.None,
      customElement: ButtonColors.None
    };

    this.mySubscriber = [];
    this.mySubscriber.push(
      this.designService.onReady.subscribe((message: string) => {
        this.reportPageKeys = this.designService.getPageIds();
        this.designService.notifyChangeScroll(
          this.notifier,
          this.designService.getPageIds()[0]
        );
        this.viewSelectedElement();
        this.viewCommonElement();
      })
    );
    this.mySubscriber.push(
      this.designService.onEndUpdate.subscribe((message: string) => {
        this.viewCommonElement();
      })
    );
    this.mySubscriber.push(
      this.designService.onSelectedElement.subscribe((message: string) => {
        this.viewSelectedElement();
      })
    );
    this.mySubscriber.push(
      this.designService.onEndDraw.subscribe((message: string) => {
        // 全てのボタンの色を「選択無し」の色にする
        Object.keys(this.buttonColors).forEach((propName: string) => {
          this.buttonColors[propName] = ButtonColors.None;
        });
      })
    );

    const applicationType = this.storageService.fetchApplicationType(
      REPORT_TEMPLATE___APPLICATION_TYPE
    );
    if (applicationType) {
      this.isVisol = applicationType.IsVisol;
    }
    if (this.isVisol) {
      this.loadSubjectModels();
    }

    this.reportType = this.translate.instant('objects.' + REPORT_TYPES.find(x => x.value == this.designService.getTemplateReportType()).label);

    this.translatedEquipmentType =
      Array.isArray(this.equipmentType)
        ?
        this.equipmentType.map((r) => { return this.translate.instant('objects.' + r); }).join('\n') :
        (this.equipmentType ?
          (this.equipmentType as string).split(',').map((r) => { return this.translate.instant('objects.' + r.replace('&amp;', '&')); }).join(',') :
          '');
  }

  public applications: string[] = [this.translate.instant('reportPreview.ViSOL'), this.translate.instant('reportPreview.IPM'), this.translate.instant('reportPreview.Generic')];
  public application: string;

  public isVisol: boolean;

  private mySubscriber: Subscription[];
  private notifier: Notifier;
  public reportPageKeys: Array<string>;
  public buttonColors: { [buttonName: string]: string };
  public selectedPropPanels: PropertyPanelDef[];

  public selectedElements: any = [];
  public selectedPage: any;
  public isActionCut: boolean;
  private lastClickedPoint: MousePoint;

  public showSideNav: boolean;
  public subjectModelsMaster: SubjectModelWrapper[] = [];
  public subjectModelsCtrl = new UntypedFormControl('', []);
  public commonPropPanels: PropertyPanelDef[];
  public showMenu: string;
  public validateErrorList: ValidationErrorDetails[] = [];
  public readonly defaultTop: number = 200;
  public readonly errorListHeight: number = 26;
  public readonly displayErrorListNam: number = 3;
  public showAllItems: string;
  public navigatorToggle = [];
  public addElements = false;
  public preview: ReportPreviewMetadata;
  public customElementDetails: ReportCustomElements[];
  public isSelectedElementPageOnly = false;
  public reportType: string;
  public equipmentType = this.designService.getTemplateEquipmentType();
  public translatedEquipmentType = this.equipmentType;
  private isSaveEnableForDeletedPage: boolean;

  public get templateName(): string {
    return this.designService.getTemplateName();
  }

  public get isSmallScreen(): boolean {
    const width = window.innerWidth;
    return width <= 1200 ? true : false;
  };

  public permissionForActions: {
    [objectName: string]: {
      [actionName: string]: boolean;
    };
  };

  @Output() public closeSnackBar = new EventEmitter<any>();

  public ngOnInit() {
    this.designService.ready();
    this.application = this.getApplication();

    if (this.navigation) {
      this.showSideNav = false;
    }
    this.propertyValidate();
  }

  public ngOnDestroy() {
    this.mySubscriber.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }
  public scrollEvent(eve) {
    const pageNo = Math.floor(
      eve.target.scrollTop / (this.designService.getPaperHeightPx() + 8)
    ); // ページ間隔8pxを足す
    this.designService.notifyChangeScroll(
      this.notifier,
      this.designService.getPageIds()[pageNo]
    );
  }
  public onClick(event: MouseEvent) {
    // レポート要素のonClickイベント内で、event.stopPropagation()を実行しているので
    // レポート要素Click時はこの処理が動かない。
    // レポート要素以外の部分をクリックしたら、すべてのレポート要素の選択状態を解除する。
    this.designService.notifyDeselectAll(this.notifier, event.ctrlKey);
    this.designService.highlightElement = false; //
    this.designService.clickedPageIdForDelete = '';
    this.selectedPropPanels = [];
  }

  // エラーリストをクリックした際のイベント
  public onClickErrorList(event: MouseEvent, error: ValidationErrorDetails) {
    if (error.pageIndex !== undefined) {
      this.designService.notifySelectPage(error);
    }
    this.designService.notifySelectElement(
      this.notifier,
      error.uuid,
      event.ctrlKey
    );
  }

  public selectElement(element, event) {
    this.showAllItems = '';
    const notifier = {
      componentName: 'report-preview.component',
      ids: { pageId: element.page, elementId: element.id },
    };
    const dom: HTMLElement = this.eleref.nativeElement; // Code for autoscroll
    const elements = dom.querySelector('#pageScroll');
    elements.scroll(
      0,
      this.designService.getPaperHeightPx() *
      this.designService.getPageIds().indexOf(element.page)
    );
    this.designService.notifyChangeScroll(this.notifier, element.page);
    this.designService.notifySelectElement(notifier, element.id, event.ctrlKey);
  }
  public viewSelectedElement() {
    this.isSelectedElementPageOnly = false;
    const selectedElemProps: ReportProperties[] =
      this.designService.getSelectedElementProperties();
    this.selectedPropPanels = [];
    selectedElemProps.forEach((reportProperties: ReportProperties) => {
      const type = this.getPanelType(reportProperties.getPropOrElemType());
      if (this.designService.clickedPageIdForDelete || type !== 'Page') {
        this.selectedPropPanels.push({
          propId: reportProperties.getId(),
          title: reportProperties.getTitle(),
          propOrElemType: reportProperties.getPropOrElemType(),
          mapTableData: reportProperties.getMapProperties(),
        });
        this.showMenu = 'properties';
      }
    });
    if (this.selectedPropPanels.map(x => x.propOrElemType).every((propOrElemType) => { return propOrElemType === ElementType.Page; })) {
      this.isSelectedElementPageOnly = true;
    }
    else {
      this.designService.clickedPageIdForDelete = '';
    }
  }
  public getPanelType(propOrElemType: PropOrElemType): string {
    const types: ElementType[] = [
      ElementType.Page,
      ElementType.Text,
      ElementType.Line,
      ElementType.Image,
      ElementType.Rectangle,
      ElementType.Summary,
      ElementType.SubTotal,
      ElementType.Picture,
      ElementType.DataPlate,
      ElementType.InteractiveIso,
      ElementType.DropDownList,
      ElementType.CustomElement
    ];
    for (const type of types) {
      if (propOrElemType.indexOf(type) === 0) {
        return type;
      }
    }
    return null;
  }

  // Use this to select the newly added page. Due to the delay of app-report-page-container, I have to put the code into ngAfterViewChecked.
  private nextPage: number = -1;
  public ngAfterViewChecked() {
    if (this.nextPage !== -1) {
      this.designService.notifySelectPage({ pageIndex: this.nextPage });
      this.nextPage = -1;
    }
  }

  public onSelectObjectMenu(objectName: string, action: string, designId?: string, designIdName?: string) {
    this.designService.highlightElement = true;
    switch (action) {
      case 'add':
        Object.keys(this.buttonColors).forEach((propName: string) => {
          this.buttonColors[propName] = ButtonColors.None;
        });
        const pageId = this.designService.getViewPageId();
        let index = this.designService.getPageIds().findIndex((id) => { return id === pageId; });
        this.designService.notifyAddPage(this.notifier);
        this.nextPage = index + 1;
        break;

      case 'draw':
        const elementType: ElementType = ElementType.cast(objectName);
        if (this.buttonColors[objectName] === ButtonColors.None) {
          Object.keys(this.buttonColors).forEach((propName: string) => {
            this.buttonColors[propName] = ButtonColors.None;
          });
          this.buttonColors[objectName] = ButtonColors.Selected;
          if (designId) {
            this.designService.notifyDrawMode(this.notifier, elementType, designId, designIdName);
          }
          else {
            this.designService.notifyDrawMode(this.notifier, elementType);
          }
        } else {
          this.designService.notifyDrawCancel(this.notifier);
        }
        break;

      case 'delete':
        Object.keys(this.buttonColors).forEach((propName: string) => {
          this.buttonColors[propName] = ButtonColors.None;
        });
        this.designService.notifyDelete(this.notifier);
        break;
      case 'selectAll':
      case 'selectByPage':
        break;

      default:
        break;
    }
  }
  public openImageUploadDialog() {
    this.closeSnackBar.emit({ isDestory: false, setTime: 3000000 });
    const dialogRef = this.dialog.open(
      ReportTemplateImageUploadDialogComponent,
      {
        width: '800px',
        height: '90%',
        autoFocus: false,
        data: {
          templateId: this.designService.getTemplateId(),
          usedFileNameList: this.getUsedFileNameList(),
        },
      }
    );
    dialogRef.componentInstance.changeImages.subscribe(() => {
      this.closeSnackBar.emit({ isDestory: false, setTime: 3000000 });
      this.designService.loadReportTemplateImages();
    });
  }
  public getUsedFileNameList(): string[] {
    const usedFileNameList = [];
    const originTemplate: TemplateDefinition =
      this.designService.getOriginTemplate().template;
    const editingTemplate: TemplateDefinition =
      this.designService.getTemplate();

    originTemplate.header.images.forEach((image: TemplateImage) => {
      usedFileNameList.push(image.src.split('/').pop());
    });
    originTemplate.bodies.forEach((page: TemplatePageSection) => {
      page.images.forEach((image: TemplateImage) => {
        if (image.src) {
          usedFileNameList.push(image.src.split('/').pop());
        }
      });
    });
    originTemplate.footer.images.forEach((image: TemplateImage) => {
      usedFileNameList.push(image.src.split('/').pop());
    });
    editingTemplate.header.images.forEach((image: TemplateImage) => {
      usedFileNameList.push(image.src.split('/').pop());
    });
    editingTemplate.bodies.forEach((page: TemplatePageSection) => {
      page.images.forEach((image: TemplateImage) => {
        if (image.src) {
          usedFileNameList.push(image.src.split('/').pop());
        }
      });
    });
    editingTemplate.footer.images.forEach((image: TemplateImage) => {
      usedFileNameList.push(image.src.split('/').pop());
    });
    return Array.from(new Set(usedFileNameList));
  }
  public isModelSelectedNone(): boolean {
    if (!this.reportPageKeys) {
      return false;
    } else {
      return (
        this.reportPageKeys.length === 0 ||
        !this.designService.getTemplateSubjectModelIds() ||
        this.designService.getTemplateSubjectModelIds().length === 0
      );
    }
  }
  public cancel() {
    this.router.navigate(['/']);
  }

  public changeApplication() {
    this.designService.setTemplateApplication(this.application);
  }
  public getApplication() {
    return this.designService.getTemplateApplication();
  }

  public deleteCustomElement(componentId: string) {
    this.spinnerService.show(this.spinnerService.mainSpinnerId);
    this.reportComponentService.deleteCustomTemplate(componentId).subscribe(res => {
      this.designService.reportCustomTemplates = this.designService.reportCustomTemplates.filter(x => x.id != componentId);
    },
      (error) => {
        this.spinnerService.hide(this.spinnerService.mainSpinnerId);
        this.snackBar.open(this.translate.instant('reportPreview.deletefailed'));
      },
      () => {
        this.spinnerService.hide(this.spinnerService.mainSpinnerId);
      })
  }

  @HostListener('window:keydown', ['$event'])
  public onKeyPress($event: any) {
    if ($event.target.tagName === 'INPUT' || $event.target.tagName === 'TEXTAREA') {
      return;
    }

    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 67) {
      this.copySelectedElements();
    }
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 86) {
      this.PasteCopiedElements();
    }
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 88) {
      this.cutSelectedElements();
    }
    if ($event.key === 'Delete') {
      this.deleteSelectedElement();
    }
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 90) {
      if (this.designService.templateForUndo.length > 0) {
        this.undoElement();
      }
    }
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 89) {
      if (this.designService.templateForRedo.length > 0) {
        this.redoElement();
      }
    }
  }
  @HostListener('mousemove', ['$event'])
  public handleMousemove(event) {
    this.lastClickedPoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY,
    };
  }
  // @HostListener('window:mousedown', ['$event'])
  public onMouseDown($event: MouseEvent) {
    console.log('select');
    const selectedElements: any =
      this.designService.getSelectedElementProperties();
    console.log(selectedElements);
    console.log(selectedElements[0].getId());

    console.log(this.selectedPage);

    const summaryIds = this.designService.getSummaryIds(
      selectedElements[0].getId(),
      'Body'
    );
    summaryIds.forEach((id: string) => {
      console.log('summary ids');
      console.log(id);
      console.log(this.designService.getSummary(id));
    });

    const subTotalIds = this.designService.getSubTotalIds(
      selectedElements[0].getId(),
      'Body'
    );
    subTotalIds.forEach((id: string) => {
      console.log('subtotal ids');
      console.log(id);
      console.log(this.designService.getSubTotal(id));
    });
    // commProps.forEach((reportProperties: ReportProperties) => {
    //  this.commonPropPanels.push({
    //    propId: reportProperties.getId(),
    //    title: reportProperties.getTitle(),
    //    tableData: reportProperties.getProperties()
    //  });
    // });

    this.selectedPage = selectedElements.find(
      (el) => el.type.toLowerCase() === 'page'
    );
  }

  public cutSelectedElements(): void {
    this.isActionCut = true;
    this.copySelectedElements();
  }

  public copySelectedElements(): void {
    const selectedElements: any =
      this.designService.getSelectedElementProperties();
    this.selectedElements = selectedElements.filter(
      (el) => el.type.toLowerCase() !== 'page'
    );
    this.selectedPage = selectedElements.find(
      (el) => el.type.toLowerCase() === 'page'
    );
  }

  public PasteCopiedElements(): void {
    const template = { ...this.designService.originTemplate };
    template.reportTemplateContent = JSON.stringify(
      this.designService.getTemplate()
    );
    this.designService.templateForUndo.push(template);
    this.selectedElements.forEach((element, index) => {
      const baseDef = Object.assign({}, element.baseDef);

      const previewMetaData: ReportPreviewMetadata =
        this.designService.fitVirtualGrid({
          topPx: this.lastClickedPoint.onElementY,
          leftPx: this.lastClickedPoint.onElementX,
          widthPx: 0,
          heightPx: 0,
        });

      const units = new ReportUnits(
        this.designService.getOriginTemplate().template, this.translate, this.langService
      );
      baseDef.id = '';
      baseDef.y = units.fromPx('sizeUnit', previewMetaData.topPx);
      baseDef.x = units.fromPx('sizeUnit', previewMetaData.leftPx);
      if (this.isActionCut) {
        this.isActionCut = false;
        this.deleteCopiedElement(element);
      }
      this.designService.duplicateElements(
        element.location,
        element.type,
        baseDef,
        this.selectedPage.uuid
      );
    });
  }

  public deleteCopiedElement(el: any): void {
    this.designService.deleteElement(el.uuid, el.location);
  }

  public deleteSelectedElement(): void {
    const elementUndo = { ...this.designService.originTemplate };
    elementUndo.reportTemplateContent = JSON.stringify(
      this.designService.getTemplate()
    );
    this.designService.templateForUndo.push(elementUndo);

    const selectedElemProps: ReportProperties[] =
      this.designService.getSelectedElementProperties();

    let isDeletingPage = false;

    if (selectedElemProps.length == 1) {
      const type = this.getPanelType(selectedElemProps[0].getPropOrElemType());
      if (type === 'Page') {
        isDeletingPage = true;
        const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
          width: '600px',
          minWidth: '400px',
          maxHeight: '800px',
          data: { target: 'pageDelete' }
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.designService.deletePage(this.designService.clickedPageIdForDelete);
            this.selectedPropPanels = [];
            this.designService.clickedPageIdForDelete = '';
            this.isSaveEnableForDeletedPage = true;
            this.showMenu = '';
          }
        });
      }
    }

    this.designService.notifyDelete(this.notifier);
    if (!isDeletingPage) {
      this.showMenu = '';
    }
    this.propertyValidate();
  }
  public undoElement(): void {
    const templateRedo = { ...this.designService.originTemplate };
    templateRedo.reportTemplateContent = JSON.stringify(
      this.designService.getTemplate()
    );
    this.designService.templateForRedo.push(templateRedo);

    const element = JSON.parse(
      JSON.stringify(this.designService.templateForUndo.pop())
    );
    element.template = element.reportTemplateContent
      ? JSON.parse(element.reportTemplateContent)
      : new TemplateDefinition();
    this.designService.loadReportTemplate(element, true);
  }

  public redoElement(): void {
    const elementUndo = { ...this.designService.originTemplate };
    elementUndo.reportTemplateContent = JSON.stringify(
      this.designService.getTemplate()
    );
    this.designService.templateForUndo.push(elementUndo);

    const element = JSON.parse(
      JSON.stringify(this.designService.templateForRedo.pop())
    );
    element.template = element.reportTemplateContent
      ? JSON.parse(element.reportTemplateContent)
      : new TemplateDefinition();
    this.designService.loadReportTemplate(element, true);
  }

  public onNavigationClose(): void {
    this.navigation.close();
  }

  public onNavigationToggle(): void {
    this.navigation.toggle();
  }

  public getSortedSubjectModels(): SubjectModelWrapper[] {
    return this.subjectModelsCtrl.value
      .slice()
      .sort((a, b) => a.order - b.order);
  }

  public loadSubjectModels(): void {
    let subjectModels: SubjectModelListItem[];
    this.subjectModelService.getSubjectModels().subscribe(
      (sb) => {
        subjectModels = sb;
      },
      (error) => {
        console.log(error);
      },
      () => {
        this.subjectModelsMaster = subjectModels.map(
          (item) => new SubjectModelWrapper(item)
        );
        this.designService.getTargetIdList().subscribe(
          (Idl) => {
            this.subjectModelsCtrl.setValue(
              this.initSubjectModels(this.subjectModelsMaster, Idl)
            );
          },
          (error) => {
            console.log(error);
          },
          () => { }
        );
      }
    );
  }

  public changeTargetSubjectModels() {
    this.spinnerService.show(this.spinnerService.mainSpinnerId);
    let updateIdList: UpdateIdList = new UpdateIdList();
    updateIdList = this.updateSubjectModelTemplate(
      this.subjectModelsCtrl.value,
      updateIdList
    );
    updateIdList.etag = this.designService.getTemplateEtag();
    // 通信が発生したら、sessionのsnack barをクリアする。
    this.closeSnackBar.emit({ isDestory: false, setTime: 3000000 });

    this.designService.updateModelsForTemplates(updateIdList).subscribe(
      (item) => {
        this.subjectModelsCtrl.setValue(
          this.initSubjectModels(this.subjectModelsMaster, item.updateResult)
        );
        this.designService.setTemplateSubjectModelIds(
          item.updateResult.map((targetIdList) => targetIdList.subjectModelId)
        );
        this.designService.setTemplateEtag(item.etag);
      },
      (error) => {
        console.log(error);
        this.snackBar.open(this.translate.instant('reportPreview.savefailed'));
        this.spinnerService.hide(this.spinnerService.mainSpinnerId);
      },
      () => {
        this.snackBar.open(this.translate.instant('reportPreview.savedsuccessfully'));
        this.spinnerService.hide(this.spinnerService.mainSpinnerId);
      }
    );
  }

  public initSubjectModels(
    masters: SubjectModelWrapper[],
    selection: ResponseIdList[]
  ): SubjectModelWrapper[] {
    if (!masters || !selection) {
      //console.log(masters);
      //console.log(selection);
      return [];
    }
    const ids: string[] = selection.map(
      (targetIdList) => targetIdList.subjectModelId
    );
    const selected: SubjectModelWrapper[] = [];
    ids.forEach((id, index) => {
      masters.forEach((master) => {
        if (id === master.subjectModel.id) {
          selected.push(master);
          master.order = index;
        }
      });
    });
    return selected;
  }

  public updateSubjectModelTemplate(
    subjectModelValue: SubjectModelWrapper[],
    updateIdList: UpdateIdList
  ): UpdateIdList {
    updateIdList.subjectModelIds = subjectModelValue.map(
      (wrapper) => wrapper.subjectModel.id
    );
    return updateIdList;
  }

  public viewCommonElement() {
    const commProps: ReportProperties[] =
      this.designService.getCommonProperties();
    this.commonPropPanels = [];
    commProps.forEach((reportProperties: ReportProperties) => {
      this.commonPropPanels.push({
        propId: reportProperties.getId(),
        title: reportProperties.getTitle(),
        propOrElemType: reportProperties.getPropOrElemType(),
        mapTableData: reportProperties.getMapProperties(),
      });
    });
  }

  public openTemplateSettings() {
    let selectedPanel: string;
    const dialogRef = this.dialog.open(ReportTemplateEditPropertyDialogComponent, {
      width: '800px',
      autoFocus: false,
      // heightを指定すると、mat-dialog-contentのスクロールと競合して２重スクロールになるので、指定しない
      disableClose: true,
      data: {
        commonPropPanels: this.commonPropPanels,
        templateName: this.designService.getTemplateName(),
        templateIsValid: this.designService.getTemplateIsValid(),
        templateReportTitleFormat: this.designService.getTemplateReportTitleFormat(),
        templateReportTitleInputMode: this.designService.getTemplateReportTitleInputMode(),
        templateList: this.designService.getTemplateList()
      }
    });

    dialogRef.componentInstance.selectedPanelName.subscribe((name) => {
      selectedPanel = name;
    });

    dialogRef.componentInstance.changeCommonProp.subscribe((data) => {
      if (data) {
        this.designService.notifyUpdateCommonProperties(this.notifier, data);
        switch (selectedPanel) {
          case 'Report Font':
            const value = data.name === 'fontSize' ? Number(data.value) : String(data.value);
            this.designService.setTemplateReportFont(data.name, value);
            break;
          case 'Paper Size':
            this.designService.setTemplatePaperSize(String(data.value));
            break;
          case 'Page Margin':
            this.designService.setTemplatePageMargin(data.name, Number(data.value))
            break;
          case 'Page Header':
            this.designService.setTemplateHeaderHeight(Number(data.value));
            break;
          case 'Page Footer':
            this.designService.setTemplateFooterHeight(Number(data.value));
            break;
          default:
            break;
        }
        if (data.name === 'height') {
          this.designService.reloadReportTemplate(this.designService.getTempTemplate(), true);
        }
      }
    });
    dialogRef.componentInstance.changeTemplateName.subscribe((name) => {
      if (name && REGULAR_EXPRESSION.COMMONPATTERN.test(name)) {
        this.designService.setTemplateName(name);
      }
    });
    dialogRef.componentInstance.changeTemplateIsValid.subscribe((isValid) => {
      this.designService.setTemplateIsValid(isValid);
    });
    dialogRef.componentInstance.changeTemplateFormatMode.subscribe(formatMode => {
      this.designService.setTemplateReportTitleInputMode(formatMode);
    });
    dialogRef.componentInstance.changeTemplateTitleFormat.subscribe(titleFormat => {
      this.designService.setTemplateReportTitleFormat(titleFormat);
    });
    dialogRef.componentInstance.changeLists.subscribe(list => {
      this.designService.setTemplateReportList(list);
    });
  }

  public createErrorList(): ValidationErrorDetails[] {
    return this.designService.createErrorList();
  }

  public isDisplayObject(id: string): boolean {
    return this.designService.isDisplayObject(id);
  }

  // Saveボタンの非活性判定
  public buttonDisabled(): boolean {
    return (!this.isChangeTemplate() || this.propertyValidate()) && !this.isSaveEnableForDeletedPage;
  }

  // プロパティのバリデーションチェックを行う
  public propertyValidate(): boolean {
    if (!this.isVisol) {
      return false;
    }

    const data = this.designService.reportProperties;
    const errorValueList = [];

    if (!data) {
      return false;
    }

    // metadataValidateで引っかかったエラーをリストに格納
    data.forEach((list) => {
      list.getProperties().forEach((metadata) => {
        const metadataValidate = this.metadataValidate(metadata);
        if (metadataValidate !== null && metadataValidate.invalid) {
          if (metadataValidate.hasError('required')) {
            errorValueList.push({
              property: list,
              metadata,
              error: ERROR_MESSAGE.REQUIRED,
            });
          } else if (metadataValidate.hasError('pattern')) {
            errorValueList.push({
              property: list,
              metadata,
              error: ERROR_MESSAGE.PATTERN,
            });
          } else if (metadataValidate.hasError('syntax')) {
            errorValueList.push({
              property: list,
              metadata,
              error: ERROR_MESSAGE.JSON,
            });
          }
        }
      });
    });

    // エラーリストから画面に表示されているオブジェクトに絞る
    const displayMetadata = errorValueList.filter((val) =>
      this.isDisplayObject(val.property.uuid)
    );

    // 型成形
    const metaErrorList = displayMetadata.map(
      (meta) =>
        new ValidationErrorDetails(
          meta.property,
          meta.metadata.name,
          meta.error,
          this.translate,
          this.langService,
        )
    );

    // isValidationErrorで引っかかったオブジェクトをリストに格納
    //TODO need to implement error details for generic table changes
    this.validateErrorList = this.createErrorList().concat(metaErrorList);

    // 項目ごとにグルーピング
    const details: ValidationErrorDetails[] = [];
    this.validateErrorList.forEach((meta) => {
      if (
        details.length === 0 ||
        !details.some((val) => val.uuid === meta.uuid)
      ) {
        details.push(meta);
      } else {
        details.forEach((val) => {
          if (val.uuid === meta.uuid) {
            val.validationErrorMessage.push(meta.validationErrorMessage[0]);
          }
        });
      }
    });
    this.validateErrorList = details;

    // Page順に並び変え
    this.validateErrorList = this.validateErrorList.sort((n1, n2) => {
      if ((n1.location === 'Header' && n2.pageIndex) || n1.pageIndex > n2.pageIndex) {
        return 1;
      }
      if ((n1.location === 'Footer' && n2.pageIndex) || n1.pageIndex < n2.pageIndex) {
        return -1;
      }
      return 0;
    });

    return this.validateErrorList.length > 0;
  }

  public metadataValidate(metadata: PropertyPreviewMetadata): UntypedFormControl {
    let validators = new Array<ValidatorFn>();

    if (metadata.isRequired) {
      validators.push(CustomValidators.requiredTrimmed());
    }

    if (metadata.isFieldType('Text')) {
      const textField = new UntypedFormControl('', this.designService.validatorText());
      textField.setValue(metadata.value);
      return textField;
    } else if (metadata.isFieldType('Number')) {
      validators = validators.concat(this.designService.validatorNumber());
      const numberField = new UntypedFormControl('', validators);
      numberField.setValue(metadata.value);
      return numberField;
    } else if (metadata.isFieldType('Color')) {
      const colorField = new UntypedFormControl(
        '',
        this.designService.validatorColor()
      );
      colorField.setValue(metadata.value);
      return colorField;
    } else if (metadata.isFieldType('Integer')) {
      validators = validators.concat(this.designService.validatorInteger());
      const integerField = new UntypedFormControl('', validators);
      integerField.setValue(metadata.value);
      return integerField;
    } else if (metadata.isFieldType('TextArea')) {
      const textAreaField = new UntypedFormControl(
        '',
        this.designService.validatorTextArea()
      );
      textAreaField.setValue(metadata.value);
      return textAreaField;
    } else if (metadata.isFieldType('JSON')) {
      validators = validators.concat(JSONSyntaxValidator.invalidJson);
      const jsonField = new UntypedFormControl('', validators);
      jsonField.setValue(metadata.value);
      return jsonField;
    } else if (metadata.isFieldType('Multi')) {
      validators = validators.concat(this.designService.validatorMulti());
      const jsonField = new UntypedFormControl('', validators);
      return jsonField;
    } else if (metadata.isFieldType('Select')) {
      const selectField = new UntypedFormControl('', validators);
      // Select要素（ドロップダウンリスト等）だけ、HTML側のvalue属性をオートバインディングする書き方にしても
      // 初期値が画面に表示されない現象が起きるため、setValue()を用いて値を設定する。
      selectField.setValue(metadata.value);
      if (metadata.name === 'dynamicDataField') {
        if (this.isVisol) {
          return null;
        }
        // リストの値を動的に設定
        metadata.selectItems = this.designService.getReportDynamicDataFields();
      }
      if (metadata.name === 'dynamicId') {
        if (!this.isVisol) {
          return null;
        }
        // リストの値を動的に設定
        metadata.selectItems = this.designService.getReportDynamicDataFields();
      }
      return selectField;
    } else if (metadata.isFieldType('CheckBox')) {
      validators = validators.concat(this.designService.validatorCheckBox());
      const checkBoxField = new UntypedFormControl(
        '',
        this.designService.validatorCheckBox()
      );
      checkBoxField.setValue(metadata.value);
      return checkBoxField;
    }
    return null;
  }

  public saveTemplate() {
    if (this.validateErrorList.length <= 0) {
      this.spinnerService.show(this.spinnerService.mainSpinnerId);
      this.closeSnackBar.emit({ isDestory: false, setTime: 3000000 });
      this.designService.saveReportTemplate().subscribe(
        (template: ReportTemplate) => {
          if (!template) {
            template = this.designService.getOriginTemplate();
            template.template = this.designService.getTemplate();
          }
          if (this.isVisol) {
            template.application = 'ViSOL';
          }
          this.designService.reloadTemplate(template);
        },
        (error) => {
          console.log(error);
          this.snackBar.open(this.translate.instant('reportPreview.savefailed'));
          this.spinnerService.hide(this.spinnerService.mainSpinnerId);
        },
        () => {
          this.snackBar.open(this.translate.instant('reportPreview.savedsuccessfully'));
          this.spinnerService.hide(this.spinnerService.mainSpinnerId);
          this.utilsService.updateHeaderName();
          this.isSaveEnableForDeletedPage = false;
        }
      );
    } else {
      const dialogRef = this.dialog.open(
        EditableTextsValidationDialogComponent,
        {
          width: '410px',
          height: '220px',
        }
      );
    }
  }
  public isChangeTemplate(): boolean {
    return this.designService.isChangeTemplate();
  }

  // errorMessageの数からtopの値を算出
  public getTopValue(elemName: string): string {
    let value = this.defaultTop;
    let errorNum = 0;
    if (this.validateErrorList.length > 0) {
      this.validateErrorList.forEach((error) => {
        errorNum = errorNum + error.validationErrorMessage.length;
      });
    }
    if (errorNum <= this.displayErrorListNam) {
      value = errorNum * this.errorListHeight + this.defaultTop;
    } else {
      value = this.displayErrorListNam * this.errorListHeight + this.defaultTop;
    }
    value =
      elemName === 'navigator'
        ? value - 35
        : elemName === 'add'
          ? value + 45
          : value; // adjusting top based on element
    return value + 'px';
  }

  // errorMessageの数からheightの値を算出
  public getErrorListHeight(length: number): string {
    return length * this.errorListHeight + 'px';
  }

  // scrollを発生させるかどうか
  public isScroll(): boolean {
    let errorNum = 0;
    if (this.validateErrorList.length > 0) {
      this.validateErrorList.forEach((error) => {
        errorNum = errorNum + error.validationErrorMessage.length;
      });
    }
    return errorNum > 3 ? true : false;
  }

  public redirectCustomELement() {
    this.designService.isRedirectingToCustomElement = true;
    this.router.navigateByUrl('/create-custom-element');
  }

  //Delete確認ダイアログ
  public deleteElement(): void {

    let targetTranslation: string;
    if (this.selectedPropPanels.length === 1) {
      targetTranslation = this.translate.instant('reportPreview.element');
    } else {
      targetTranslation = this.translate.instant('reportPreview.elements');
    }

    const deleteNames = [];
    for (let i = 0; i < this.selectedPropPanels.length; i++) {
      deleteNames.push(this.selectedPropPanels[i].title);
    }

    event.stopPropagation();
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { target: targetTranslation, deleteNames: deleteNames }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteSelectedElement();
      }
    });
  }
}

